import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { HttpError } from "../lib/api-client.js";
import { shouldRetryQuery } from "../lib/query-retry.js";
import { RouteTitle } from "../components/RouteTitle.js";
import { AppShell } from "../features/layout/AppShell.js";
import { Login } from "../features/auth/Login.js";
import { NotFound } from "../features/layout/NotFound.js";
import { useAuth } from "../features/auth/useAuth.js";
import { LiveView } from "../features/live-view/LiveView.js";
import { RepoDetail } from "../features/repos/RepoDetail.js";
import { RepoList } from "../features/repos/RepoList.js";
import { RunDetail } from "../features/run-detail/RunDetail.js";
import { RunList } from "../features/run-list/RunList.js";
import { Settings } from "../features/settings/Settings.js";
import { Stats } from "../features/stats/Stats.js";

/**
 * Any 401 from a non-auth request means the session expired or was revoked.
 * Drop the cached identity so the next render falls through to <Login/>.
 *
 * `scope` is the first segment of the query key / mutation key. `auth`-scoped
 * operations are skipped: a rejected login is a 401 by design, and `auth.me`
 * already maps its own 401 to a null user.
 */
function handleUnauthorized(err: unknown, scope: unknown): void {
  if (err instanceof HttpError && err.status === 401 && scope !== "auth") {
    queryClient.setQueryData(["auth", "me"], null);
  }
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (err, query) => handleUnauthorized(err, query.queryKey[0]),
  }),
  // Mutations need the same handler: a pause/resume/run-now/regenerate fired
  // after the session was revoked otherwise renders an inline "not signed in"
  // error and leaves the operator on a dashboard they can no longer act on,
  // until some polling query happens to 401 and trip the queryCache branch.
  mutationCache: new MutationCache({
    onError: (err, _vars, _ctx, mutation) =>
      handleUnauthorized(err, mutation.options.mutationKey?.[0]),
  }),
  defaultOptions: {
    queries: {
      staleTime: 1000,
      retry: shouldRetryQuery,
      refetchOnWindowFocus: false,
    },
  },
});

function AuthedRoutes() {
  const { isLoading, canView } = useAuth();

  if (isLoading) {
    return (
      <div style={{ padding: 24, color: "var(--muted)" }}>Loading…</div>
    );
  }

  if (!canView) {
    return <Login />;
  }

  return (
    <Routes>
      <Route path="/" element={<LiveView />} />
      <Route path="/runs" element={<RunList />} />
      <Route path="/runs/:traceId" element={<RunDetail />} />
      <Route path="/repos" element={<RepoList />} />
      <Route path="/repos/:owner/:name" element={<RepoDetail />} />
      <Route path="/stats" element={<Stats />} />
      <Route path="/settings" element={<Settings />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <RouteTitle />
        <AppShell>
          <AuthedRoutes />
        </AppShell>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
